import { ReactNode } from "react"
import Dialog from "@hydrologiq/shared/chakra/Dialog"

export interface Screen {
  id: string
  title: string
  component: ReactNode | (() => ReactNode) // Allow functions to prevent recreation
  canGoBack: boolean
  // Continue button properties
  canContinue?: boolean // Whether to show continue button
  onContinue?: () => void // Action to perform when continue is clicked
  continueLabel?: string // Optional custom label (defaults to "Continue")
  continueDisabled?: boolean // Whether continue button is disabled
}

export interface MultiScreenDialogProps<T extends string = string> {
  screens: Screen[]
  currentScreenId: T
  onClose: () => void
  open: boolean
  setOpen: (open: boolean) => void
  trigger?: ReactNode
  fullscreen?: boolean
  onBack?: () => void
  Provider?: React.ComponentType<{ children: ReactNode }>
}

/**
 * MultiScreenDialog - A dialog that manages multiple screens with navigation
 *
 * Features:
 * - Renders current screen based on currentScreenId
 * - Shows back button when canGoBack is true
 * - Preserves screen state through parent component
 * - Smooth screen transitions
 *
 * Usage:
 * ```tsx
 * const [currentScreen, setCurrentScreen] = useState('main')
 *
 * const screens = [
 *   {
 *     id: 'main',
 *     title: 'Main Screen',
 *     component: <MainForm />,
 *     canGoBack: false
 *   },
 *   {
 *     id: 'create-entity',
 *     title: 'Create Entity',
 *     component: <EntityForm onSuccess={() => setCurrentScreen('main')} />,
 *     canGoBack: true
 *   }
 * ]
 *
 * <MultiScreenDialog
 *   screens={screens}
 *   currentScreenId={currentScreen}
 *   onScreenChange={setCurrentScreen}
 *   open={open}
 *   onClose={() => setOpen(false)}
 * />
 * ```
 */
const MultiScreenDialog = <T extends string = string>({
  screens,
  currentScreenId,
  onClose,
  open,
  setOpen,
  trigger,
  fullscreen = false,
  onBack,
  Provider,
}: MultiScreenDialogProps<T>) => {
  const currentScreen = screens.find((screen) => screen.id === currentScreenId)
  if (!currentScreen) {
    console.error(`Screen with id "${currentScreenId}" not found`)
    return null
  }

  // Render component - if it's a function, call it to get the ReactNode
  const renderedComponent =
    typeof currentScreen.component === "function" ? currentScreen.component() : currentScreen.component

  const DialogProvider = Provider ?? (({ children }: { children: ReactNode }) => children)

  return (
    <DialogProvider>
      <Dialog
        title={currentScreen.title}
        open={open}
        setOpen={(isOpen) => {
          setOpen(isOpen)
        }}
        trigger={trigger}
        footer={currentScreen.canContinue}
        loading={false}
        disabled={false}
        fullscreen={currentScreen.id === "calendar" ? true : fullscreen}
        onBack={currentScreen.canGoBack ? onBack : undefined}
        onClose={onClose}
        showCloseButton={!currentScreen.canGoBack}
        showBackButton={currentScreen.canGoBack}
        showContinueButton={currentScreen.canContinue}
        onContinue={currentScreen.onContinue}
        continueLabel={currentScreen.continueLabel}
        continueDisabled={currentScreen.continueDisabled}
      >
        {renderedComponent}
      </Dialog>
    </DialogProvider>
  )
}

export default MultiScreenDialog
